import { UseQueryOptions, useQuery } from "@tanstack/react-query";

import { ObjectPoolManifestContext } from "lib/shared/object-pool/manifest";

import { BoundQueryClient, useBoundQueryClient } from "./queryCache";

function getContextKey(context: ObjectPoolManifestContext): string {
  if (!context) return "global";
  switch (context.type) {
    case "global":
      return "global";
    case "review":
      return `review:${context.entityId}`;
    case "rangeSet":
      return `rangeSet:${context.entityId}`;
    default:
      return "global";
  }
}

function getContextCommitQueryKey(context: ObjectPoolManifestContext) {
  return ["object", "manifest", "commit", getContextKey(context)];
}

export function markContextPending(
  boundQueryClient: BoundQueryClient,
  context: ObjectPoolManifestContext,
) {
  const { queryClient } = boundQueryClient;
  queryClient.setQueryData(getContextCommitQueryKey(context), false);
}

export function markContextCommitted(
  boundQueryClient: BoundQueryClient,
  context: ObjectPoolManifestContext,
) {
  const { queryClient } = boundQueryClient;
  queryClient.setQueryData(getContextCommitQueryKey(context), true);
}

export function useIsContextCommitted(
  context: ObjectPoolManifestContext,
  opts: Omit<UseQueryOptions<boolean>, "queryKey" | "queryFn"> = {},
) {
  const boundQueryClient = useBoundQueryClient();
  const { queryClient } = boundQueryClient;
  const queryKey = getContextCommitQueryKey(context);
  const query = useQuery<boolean>({
    queryKey,
    // Synchronous local read; never fetches from network
    queryFn: () => (queryClient.getQueryData(queryKey) as boolean | undefined) ?? false,
    staleTime: Infinity,
    gcTime: Infinity,
    ...opts,
  });
  return Boolean(query.data);
}

export { getContextKey };
